/**
 * Blog Page - Frontend listing page inspired by CDFPI
 * 
 * Features:
 * - Featured articles carousel
 * - Category filtering
 * - Search functionality
 * - View modes (by category / all articles)
 * - Responsive grid layout
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

import { Icons } from '../components/Icons';
import { useLanguage } from '../contexts/LanguageContext';
import './Blog.css';

// Types
interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image: string | null;
  reading_time: number;
  views_count: number;
  published_at: string | null;
  is_featured: boolean;
  tags: string[];
  category: {
    id: string;
    name: string;
    slug: string;
    color: string | null;
  } | null;
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color: string | null;
  description: string | null;
  articles_count: number;
}

// API URL
const API_URL = '/api/blog';

// Check if cover image is valid (absolute URL)
function hasValidCover(cover_image: string | null): boolean {
  return !!cover_image && cover_image.startsWith('http');
}

// Placeholder component for articles without cover
function ArticlePlaceholder({ color, className }: { color: string | null; className?: string }) {
  const baseColor = color || '#8b5cf6';
  return (
    <div
      className={`blog-placeholder ${className || ''}`}
      style={{
        background: `linear-gradient(135deg, ${baseColor}15 0%, ${baseColor}25 100%)`,
      }}
    >
      <div className="blog-placeholder__circles">
        <div className="blog-placeholder__circle blog-placeholder__circle--1" style={{ background: `${baseColor}20` }} />
        <div className="blog-placeholder__circle blog-placeholder__circle--2" style={{ background: `${baseColor}15` }} />
        <div className="blog-placeholder__circle blog-placeholder__circle--3" style={{ background: `${baseColor}10` }} />
      </div>
      <div className="blog-placeholder__icon" style={{ color: baseColor, background: `${baseColor}15` }}>
        <Icons.FileText size={32} />
      </div>
    </div>
  );
}

export function Blog() {
  const { language } = useLanguage();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'categories' | 'all'>('all');

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [articlesRes, categoriesRes, featuredRes] = await Promise.all([
        fetch(`${API_URL}?action=articles&limit=50&lang=${language}`),
        fetch(`${API_URL}?action=categories&lang=${language}`),
        fetch(`${API_URL}?action=featured&limit=3&lang=${language}`),
      ]);

      if (articlesRes.ok) {
        const data = await articlesRes.json();
        setArticles(data.data || []);
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data.data || []);
      }

      if (featuredRes.ok) {
        const data = await featuredRes.json();
        setFeaturedArticles(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Normalize text for search (remove accents)
  const normalizeText = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Filter articles
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      // Category filter
      if (selectedCategory && article.category?.id !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = normalizeText(searchQuery);
        const matchesTitle = normalizeText(article.title).includes(query);
        const matchesExcerpt = normalizeText(article.excerpt).includes(query);
        const matchesTags = article.tags?.some(tag => normalizeText(tag).includes(query));
        if (!matchesTitle && !matchesExcerpt && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }, [articles, selectedCategory, searchQuery]);

  // Group articles by category
  const articlesByCategory = useMemo(() => {
    const grouped: Record<string, BlogArticle[]> = {};
    categories.forEach(cat => {
      grouped[cat.id] = filteredArticles.filter(a => a.category?.id === cat.id);
    });
    return grouped;
  }, [filteredArticles, categories]);

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-page__loading">
          <Icons.Loader size={40} className="spinning" />
          <p>Chargement des articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      {/* Hero */}
      <section className="blog-hero">
        <div className="blog-hero__content">
          <h1>
            Le <span className="blog-hero__gradient">Blog</span> Edouard.ai
          </h1>
          <p>
            Articles, guides et analyses pour vous aider a investir
            intelligemment dans les crypto-monnaies.
          </p>

          {/* Search */}
          <div className="blog-hero__search">
            <Icons.Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value) setViewMode('all');
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}>
                <Icons.X size={18} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && !searchQuery && !selectedCategory && (
        <section className="blog-featured">
          <div className="blog-container">
            <h2 className="blog-section-title">
              <Icons.Star size={24} />
              A la une
            </h2>

            <div className="blog-featured__grid">
              {featuredArticles.map((article, index) => (
                <a
                  key={article.id}
                  href={`https://edouard.ai/${language === 'fr' ? 'fr' : 'en'}/blog/${article.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`blog-featured__card ${index === 0 ? 'blog-featured__card--main' : ''}`}
                >
                  <div className="blog-featured__image">
                    {hasValidCover(article.cover_image) ? (
                      <img src={article.cover_image!} alt={article.title} />
                    ) : (
                      <ArticlePlaceholder color={article.category?.color || null} />
                    )}
                    <div className="blog-featured__overlay" />
                  </div>

                  <div className="blog-featured__content">
                    {article.category && (
                      <span
                        className="blog-featured__category"
                        style={{
                          background: `${article.category.color}30`,
                          color: article.category.color || '#8b5cf6',
                        }}
                      >
                        {article.category.name}
                      </span>
                    )}
                    <h3>{article.title}</h3>
                    {index === 0 && <p>{article.excerpt}</p>}
                    <div className="blog-featured__meta">
                      <span><Icons.Calendar size={14} /> {formatDate(article.published_at)}</span>
                      <span><Icons.Clock size={14} /> {article.reading_time} min</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="blog-categories">
        <div className="blog-container">
          <div className="blog-categories__header">
            <div className="blog-categories__filters">
              <button
                className={`blog-categories__filter ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                Tous
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`blog-categories__filter ${selectedCategory === category.id ? 'active' : ''}`}
                  style={{
                    '--category-color': category.color || '#8b5cf6',
                  } as React.CSSProperties}
                  onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                >
                  {category.name}
                  <span className="blog-categories__count">{category.articles_count}</span>
                </button>
              ))}
            </div>

            <div className="blog-categories__view-toggle">
              <button
                className={viewMode === 'categories' ? 'active' : ''}
                onClick={() => setViewMode('categories')}
                title="Par categorie"
              >
                <Icons.Layout size={18} />
              </button>
              <button
                className={viewMode === 'all' ? 'active' : ''}
                onClick={() => setViewMode('all')}
                title="Tous les articles"
              >
                <Icons.Grid size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="blog-articles">
        <div className="blog-container">
          {searchQuery && (
            <div className="blog-articles__search-info">
              <span>Resultats pour "{searchQuery}"</span>
              <span className="blog-articles__count">{filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {filteredArticles.length === 0 ? (
            <div className="blog-articles__empty">
              <Icons.Search size={48} />
              <h3>Aucun article trouve</h3>
              <p>Essayez avec d'autres termes de recherche ou explorez nos categories.</p>
              <button onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}>
                Voir tous les articles
              </button>
            </div>
          ) : viewMode === 'categories' && !searchQuery ? (
            /* Category view */
            <div className="blog-articles__by-category">
              {categories.map((category) => {
                const categoryArticles = articlesByCategory[category.id] || [];
                if (categoryArticles.length === 0) return null;

                return (
                  <div key={category.id} className="blog-category-section" id={`category-${category.slug}`}>
                    <div className="blog-category-section__header">
                      <h2 style={{ color: category.color || '#8b5cf6' }}>
                        {category.name}
                      </h2>
                      <span>{categoryArticles.length} article{categoryArticles.length > 1 ? 's' : ''}</span>
                    </div>

                    <div className="blog-articles__grid">
                      {categoryArticles.slice(0, 3).map((article) => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                    </div>

                    {categoryArticles.length > 3 && (
                      <button
                        className="blog-category-section__more"
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setViewMode('all');
                        }}
                      >
                        Voir les {categoryArticles.length} articles
                        <Icons.ArrowRight size={16} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* All articles view */
            <div className="blog-articles__grid">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="blog-newsletter">
        <div className="blog-container">
          <div className="blog-newsletter__card">
            <div className="blog-newsletter__content">
              <h2>Restez informe</h2>
              <p>Recevez nos derniers articles directement dans votre boite mail.</p>
            </div>
            <div className="blog-newsletter__form">
              <input type="email" placeholder="votre@email.com" />
              <button>S'inscrire</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Article Card Component
function ArticleCard({ article }: { article: BlogArticle }) {
  const { language } = useLanguage();
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <a href={`https://edouard.ai/${language === 'fr' ? 'fr' : 'en'}/blog/${article.slug}`} target="_blank" rel="noopener noreferrer" className="blog-article-card">
      <div className="blog-article-card__image">
        {hasValidCover(article.cover_image) ? (
          <img src={article.cover_image!} alt={article.title} loading="lazy" />
        ) : (
          <ArticlePlaceholder color={article.category?.color || null} />
        )}
        {article.category && (
          <span
            className="blog-article-card__badge"
            style={{
              background: article.category.color || '#8b5cf6',
            }}
          >
            {article.category.name}
          </span>
        )}
      </div>

      <div className="blog-article-card__content">
        <h3 className="blog-article-card__title">{article.title}</h3>
        <p className="blog-article-card__excerpt">{article.excerpt}</p>

        <div className="blog-article-card__meta">
          <span><Icons.Calendar size={14} /> {formatDate(article.published_at)}</span>
          <span><Icons.Clock size={14} /> {article.reading_time} min</span>
        </div>
      </div>
    </a>
  );
}

export default Blog;
